import React, { useMemo } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Container from '../components/common/Container';
import { Briefcase, ArrowRight, TrendingUp, Award, Heart, Users, Send, Upload, X, Check, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { createCareer } from '../redux/slice/career.slice';
import * as Yup from 'yup';

// --- Shared Components (Replicated from AboutPage for consistency) ---

const FloatingImage = ({ src, alt, delay }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 50, rotate: -5 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.8, delay: delay, type: "spring" }}
            viewport={{ once: true }}
            className="relative"
        >
            <div className="absolute inset-0 border-2 border-secondary translate-x-2 md:translate-x-4 translate-y-2 md:translate-y-4 -z-10 rounded-xl md:rounded-2xl opacity-30"></div>
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-2xl group">
                <div className="absolute inset-0 bg-secondary/20 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                <img src={src} alt={alt} className="w-full h-auto object-cover grayscale group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-105" />
            </div>
        </motion.div>
    );
};

const ValueCard = ({ title, description, icon }) => {
    return (
        <motion.div
            whileHover={{ y: -10 }}
            className="bg-white p-5 md:p-8 rounded-xl md:rounded-2xl border border-primary/5 shadow-lg backdrop-blur-sm group hover:border-secondary/30 transition-all duration-300 h-full"
        >
            <div className="w-12 h-12 md:w-16 md:h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-4 md:mb-6 text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-300">
                {icon}
            </div>
            <h3 className="text-xl md:text-2xl font-serif font-bold text-primary mb-2 md:mb-4">{title}</h3>
            <p className="text-secondary text-sm sm:text-base leading-relaxed font-sans group-hover:text-primary transition-colors">{description}</p>
        </motion.div>
    );
};

// --- Form Components ---

const InputField = ({ label, type = "text", name, value, onChange, onBlur, error, touched }) => (
    <div className="group relative mb-1 md:mb-2 j_input_style">
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder=" "
            className={`peer w-full bg-transparent border-b py-2 md:py-4 text-sm md:text-base text-primary focus:outline-none focus:border-secondary transition-colors font-serif placeholder-transparent ${touched && error ? 'border-red-500' : 'border-primary/20'}`}
        />
        <label className={`absolute left-0 text-secondary text-xs md:text-sm transition-all duration-300 pointer-events-none
            peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 md:peer-placeholder-shown:text-base md:peer-placeholder-shown:top-4
            peer-focus:-top-2 peer-focus:text-[10px] md:peer-focus:text-xs peer-focus:text-secondary peer-focus:uppercase peer-focus:tracking-widest
            ${value ? '-top-2 text-[10px] md:text-xs text-secondary uppercase tracking-widest' : ''}
            ${touched && error ? 'text-red-500' : ''}`}
        >
            {label}
        </label>
        {touched && error && (
            <div className="text-red-500 text-[10px] md:text-xs mt-1 absolute right-0 bottom-2">{error}</div>
        )}
    </div>
);

const CustomSelect = ({ label, name, value, onChange, options, error, touched }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleSelect = (optionValue) => {
        onChange({ target: { name, value: optionValue } });
        setIsOpen(false);
    };

    return (
        <div className="group relative mb-4 md:mb-6">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-transparent border-b py-2 md:py-4 text-left flex items-center justify-between focus:outline-none transition-colors border-primary/20 ${isOpen ? 'border-secondary' : ''} ${touched && error ? 'border-red-500' : ''}`}
            >
                <span className={`font-serif text-sm md:text-base transition-colors ${value ? 'text-primary' : 'text-transparent'}`}>
                    {value || 'Placeholder'}
                </span>
                <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 text-light transition-transform duration-300 ${isOpen ? 'rotate-180 text-secondary' : ''}`} />
            </button>

            <label className={`absolute left-0 text-light text-xs md:text-sm transition-all duration-300 pointer-events-none
                ${value || isOpen ? '-top-2 text-[10px] md:text-xs text-secondary uppercase tracking-widest' : 'top-2 md:top-4 text-sm md:text-base'}
                ${touched && error ? 'text-red-500' : ''}`}
            >
                {label}
            </label>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute z-50 left-0 right-0 top-full mt-2 bg-white/95 backdrop-blur-xl border border-primary/10 rounded-lg md:rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto"
                    >
                        {options.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => handleSelect(option.title)}
                                className={`w-full px-4 py-2 md:px-6 md:py-3 text-left transition-colors font-serif text-xs md:text-sm border-b border-primary/5 last:border-0 hover:bg-primary/5 flex items-center justify-between ${value === option.title ? 'text-secondary bg-secondary/10' : 'text-primary'}`}
                            >
                                {option.title}
                                {value === option.title && <Check size={14} />}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
            {touched && error && (
                <div className="text-red-500 text-[10px] md:text-xs mt-1 absolute right-0 -bottom-5">{error}</div>
            )}
        </div>
    );
};

const FileUpload = ({ name, value, setFieldValue, error, touched }) => (
    <div className="mb-6 md:mb-8">
        <label className="block text-secondary text-[12px] md:text-sm font-medium uppercase tracking-widest mb-2 md:mb-4">Resume / CV</label>
        <div
            className={`relative group border border-dashed rounded-lg md:rounded-xl p-4 md:p-8 text-center transition-all duration-300 cursor-pointer
                ${error && touched ? 'border-red-500 bg-red-500/5' : 'border-primary/20 hover:border-secondary hover:bg-secondary/5'}
            `}
        >
            <input
                type="file"
                name={name}
                multiple
                onChange={(event) => setFieldValue(name, Array.from(event.currentTarget.files))}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
            />

            {value ? (
                <div className="flex flex-col gap-2">
                    {value.map((file, index) => (
                        <div key={index} className="relative z-10 flex items-center justify-between gap-2 md:gap-4 bg-white/50 p-2 rounded-lg">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-secondary/20 flex items-center justify-center text-secondary">
                                    <Briefcase size={16} />
                                </div>
                                <div className="text-left">
                                    <p className="text-primary font-serif text-xs md:text-sm truncate max-w-[150px] md:max-w-[200px]">{file.name}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const newFiles = value.filter((_, i) => i !== index);
                                    setFieldValue(name, newFiles.length > 0 ? newFiles : null);
                                }}
                                className="p-1 md:p-2 hover:bg-primary/5 rounded-full text-light hover:text-red-500 transition-colors z-30"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center gap-1 md:gap-2 text-light group-hover:text-primary transition-colors">
                    <Upload size={20} className="text-secondary opacity-70 mb-1 md:mb-2" />
                    <span className="text-xs md:text-sm">Click to Upload (PDF, DOC, Images)</span>
                </div>
            )}
        </div>
        {touched && error && (
            <div className="text-red-500 text-[10px] md:text-xs mt-2 text-center">{error}</div>
        )}
    </div>
);

const CareersPage = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.career);
    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
    const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

    const openPositions = [
        { id: 1, title: 'Senior Architect' },
        { id: 2, title: 'Project Manager' },
        { id: 3, title: 'Sales Executive' },
        { id: 4, title: 'Civil Engineer' },
        { id: 5, title: 'Interior Designer' },
        { id: 6, title: 'Digital Marketing Manager' }
    ];

    const benefits = [
        {
            icon: <TrendingUp className="w-6 h-6 md:w-8 md:h-8" />,
            title: 'Growth & Learning',
            description: 'We invest in your future with mentorship programs, workshops, and clear paths for career advancement.'
        },
        {
            icon: <Award className="w-6 h-6 md:w-8 md:h-8" />,
            title: 'Competitive Pay',
            description: 'Industry-leading compensation packages that recognize and reward your hard work and talent.'
        },
        {
            icon: <Heart className="w-6 h-6 md:w-8 md:h-8" />,
            title: 'Wellness First',
            description: 'Comprehensive health benefits and a supportive work-life balance to keep you at your best.'
        },
        {
            icon: <Users className="w-6 h-6 md:w-8 md:h-8" />,
            title: 'Dynamic Culture',
            description: 'A collaborative, inclusive environment where every voice is heard and every idea matters.'
        }
    ];

    const validationSchema = Yup.object({
        name: Yup.string().required('Full Name is required'),
        email: Yup.string().email('Invalid email').required('Email is required'),
        phone: Yup.string().matches(/^[0-9]{10}$/, '10 digits required').required('Phone is required'),
        position: Yup.string().required('Position is required'),
        experience: Yup.string().required('Experience is required'),
        resume: Yup.mixed()
            .required('Resume is required')
            .test('fileType', 'Invalid file type', (value) => {
                if (!value) return true;
                const validTypes = [
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'image/jpeg',
                    'image/png',
                    'image/webp'
                ];
                return value.every(file => validTypes.includes(file.type));
            }),
        coverLetter: Yup.string()
    });

    const formik = useFormik({
        initialValues: { name: '', email: '', phone: '', position: '', experience: '', resume: null, coverLetter: '' },
        validationSchema,
        onSubmit: (values, { resetForm }) => {
            const formData = new FormData();
            formData.append('fullname', values.name);
            formData.append('email', values.email);
            formData.append('phone', values.phone);
            formData.append('position', values.position);
            formData.append('experience', values.experience);
            if (values.resume && values.resume.length > 0) {
                values.resume.forEach((file, index) => {
                    formData.append(`images[${index}]`, file);
                });
            }
            if (values.coverLetter) formData.append('cover_latter', values.coverLetter);

            dispatch(createCareer(formData))
                .unwrap()
                .then(() => {
                    resetForm();
                })
                .catch((err) => {
                    console.error("Submission failed", err);
                });
        }
    });

    return (
        <div className="min-h-screen bg-background overflow-hidden text-primary selection:bg-secondary selection:text-white">

            {/* Hero Section (Matches AboutPage) */}
            <section className="relative h-[60vh] md:h-[80vh] flex items-center justify-center overflow-hidden">
                <motion.div style={{ y, opacity }} className="absolute inset-0">
                    <div
                        className="absolute inset-0 bg-cover bg-center scale-110"
                        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1920&auto=format&fit=crop')" }}
                    />
                    <div className="absolute inset-0 bg-white/20" /> {/* Light overlay instead of black */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                </motion.div>

                <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                    >
                        <div className="flex items-center justify-center gap-2 md:gap-4 mb-3 md:mb-6">
                            <span className="h-px w-6 md:w-12 bg-secondary" />
                            <span className="text-secondary font-bold tracking-[0.2em] uppercase text-[10px] md:text-sm">Careers at Valletta</span>
                            <span className="h-px w-6 md:w-12 bg-secondary" />
                        </div>
                        <h1 className="text-3xl md:text-7xl lg:text-7xl font-serif font-bold text-primary mb-4 md:mb-8 leading-tight drop-shadow-md">
                            Build Your <br />
                            <span className="">Design Legacy</span>
                        </h1>
                        <p className="text-sm md:text-xl text-secondary max-w-2xl mx-auto font-medium leading-relaxed px-4 drop-shadow-sm">
                            Join a team of visionaries dedicated to crafting the future of luxury living in Surat.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Main Content */}
            <section className="relative z-20 bg-background -mt-10 md:-mt-20 pt-6 md:pt-20">
                <div className="absolute top-0 left-0 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-secondary/5 blur-[80px] md:blur-[120px] rounded-full animate-pulse-slow pointer-events-none" />

                <Container>
                    {/* Culture Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 lg:gap-24 items-center mb-16 md:mb-32 pt-8 md:pt-12">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-4 md:space-y-8"
                        >
                            <div>
                                <span className="text-secondary font-bold tracking-[0.2em] uppercase text-[10px] md:text-xs block mb-2 md:mb-3">Our Culture</span>
                                <h2 className="text-3xl md:text-5xl font-serif text-primary font-bold leading-tight">More Than <br />Just a Job</h2>
                            </div>
                            <div className="space-y-3 md:space-y-4 text-secondary font-sans text-sm md:text-lg font-medium leading-relaxed">
                                <p>
                                    At <span className="text-primary font-medium">Valletta Group</span>, we believe that great architecture is born from great culture. We foster an environment where creativity thrives, bold ideas are celebrated, and every team member is empowered to make an impact.
                                </p>
                                <p>
                                    We are looking for individuals who share our obsession with detail, our passion for innovation, and our commitment to excellence. If you are ready to challenge the status quo, we want to hear from you.
                                </p>
                            </div>
                        </motion.div>

                        <FloatingImage
                            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1000&auto=format&fit=crop"
                            alt="Team Collaboration"
                            delay={0.2}
                        />
                    </div>

                    {/* Benefits Grid */}
                    <div className="mb-20 md:mb-40">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            className="text-center mb-8 md:mb-16 max-w-3xl mx-auto"
                        >
                            <span className="text-secondary font-bold tracking-[0.1em] uppercase text-[10px] md:text-sm block mb-2 md:mb-4">Why Join Us</span>
                            <h2 className="text-3xl md:text-5xl font-serif font-bold text-primary mb-3 md:mb-6">Perks & Benefits</h2>
                            <p className="text-base md:text-lg text-secondary font-medium font-sans">We take care of our own. Discover what makes working at Valletta truly rewarding.</p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                            {benefits.map((bg, idx) => (
                                <ValueCard
                                    key={idx}
                                    icon={bg.icon}
                                    title={bg.title}
                                    description={bg.description}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Application Form Section (Styled like Sustainability section in AboutPage) */}
                    <div className="mb-20 md:mb-40 bg-white rounded-xl md:rounded-[3rem] p-6 md:p-16 border border-primary/5 shadow-2xl overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-secondary/5 blur-[80px] md:blur-[150px] rounded-full pointer-events-none" />

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-16 relative z-10">

                            {/* Left Side: Text */}
                            <div className="lg:col-span-5 space-y-4 md:space-y-8">
                                <div>
                                    <span className="text-secondary font-bold tracking-[0.2em] uppercase text-[10px] md:text-xs block mb-2 md:mb-4">Start Your Journey</span>
                                    <h2 className="text-2xl md:text-5xl font-serif font-bold text-primary leading-tight">Ready to Make <br />Your Mark?</h2>
                                </div>
                                <p className="text-sm md:text-lg text-secondary leading-relaxed font-sans font-medium">
                                    Fill out the form to apply for our open positions. Please include your resume and a cover letter telling us why you'd be a great fit for Valletta.
                                </p>
                                <div className="space-y-3 md:space-y-4 pt-4 border-t border-primary/10">
                                    <p className="flex items-center gap-3 text-primary">
                                        <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-secondary">
                                            <Send size={16} />
                                        </div>
                                        <span className="text-sm md:text-base">careers@vallettagroup.com</span>
                                    </p>
                                    <p className="text-secondary text-xs md:text-sm pl-11">
                                        Prefer to email us directly? Feel free to reach out.
                                    </p>
                                </div>
                            </div>

                            {/* Right Side: Form */}
                            <div className="lg:col-span-7">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="relative"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-xl md:rounded-[2rem] blur-sm -z-10" />
                                    <form
                                        onSubmit={formik.handleSubmit}
                                        className="bg-white/80 backdrop-blur-xl p-6 md:p-12 rounded-xl md:rounded-[2rem] border border-primary/10 shadow-lg space-y-6 md:space-y-8"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                                            <InputField
                                                label="Full Name"
                                                name="name"
                                                value={formik.values.name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.errors.name}
                                                touched={formik.touched.name}
                                            />
                                            <InputField
                                                label="Email Address"
                                                type="email"
                                                name="email"
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.errors.email}
                                                touched={formik.touched.email}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                                            <InputField
                                                label="Phone Number"
                                                type="tel"
                                                name="phone"
                                                value={formik.values.phone}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.errors.phone}
                                                touched={formik.touched.phone}
                                            />
                                            <InputField
                                                label="Position Applied For"
                                                name="position"
                                                value={formik.values.position}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.errors.position}
                                                touched={formik.touched.position}
                                            />
                                        </div>

                                        <div>
                                            <InputField
                                                label="Years of Experience"
                                                name="experience"
                                                value={formik.values.experience}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.errors.experience}
                                                touched={formik.touched.experience}
                                            />
                                        </div>

                                        <FileUpload
                                            name="resume"
                                            value={formik.values.resume}
                                            setFieldValue={formik.setFieldValue}
                                            error={formik.errors.resume}
                                            touched={formik.touched.resume}
                                        />

                                        <div className="group relative pt-2 md:pt-4">
                                            <textarea
                                                name="coverLetter"
                                                value={formik.values.coverLetter}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                rows="3"
                                                placeholder=" "
                                                className="peer w-full bg-transparent border-b py-2 md:py-4 text-sm md:text-base text-primary focus:outline-none focus:border-secondary transition-colors font-serif resize-none placeholder-transparent border-primary/20"
                                            />
                                            <label className={`absolute left-0 text-secondary text-xs md:text-sm transition-all duration-300 pointer-events-none
                                                peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 md:peer-placeholder-shown:text-base md:peer-placeholder-shown:top-4
                                                peer-focus:-top-2 peer-focus:text-[10px] md:peer-focus:text-xs peer-focus:text-secondary peer-focus:uppercase peer-focus:tracking-widest
                                                ${formik.values.coverLetter ? '-top-2 text-[10px] md:text-xs text-secondary uppercase tracking-widest' : ''}`}
                                            >
                                                Cover Letter (Optional)
                                            </label>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={formik.isSubmitting || !formik.isValid}
                                            className="w-full bg-secondary text-white py-3 md:py-4 rounded-lg md:rounded-xl font-bold uppercase tracking-widest text-xs md:text-sm hover:bg-primary hover:text-white transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(134,93,54,0.3)] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4 md:mt-8"
                                        >
                                            {loading ? 'Sending...' : 'Submit Application'}
                                        </button>
                                    </form>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default CareersPage;
